import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { SidebarOpenContext } from './context'

/**
 * The pieces of the shared shell a product fills in: its parts of the top bar, and its rows in the rail. The
 * shell keeps the logo, the product tabs and the rail itself, so they stay put as the browser moves between
 * products.
 */
export enum ShellSlot {
  MENU_BUTTON = 'menuButton',
  ORG = 'org',
  ACTIONS = 'actions',
  PROFILE = 'profile',
  RAIL = 'rail',
  /** A product's own groups in the shell's phone menu, in the place of its row. */
  MENU = 'menu',
}

const SLOTS = Object.values(ShellSlot)

type SlotNodes = Partial<Record<ShellSlot, HTMLElement | null>>

/** What a product rendered inside the shared shell can reach. */
export interface ProductShell {
  /** Cai's chat sits beside the product, so the product's own assistant stays out of the way. */
  caiDocked: boolean
  /** The browser holds a session, so a product the account lacks is an offer rather than a sign-in. */
  signedIn: boolean
  slots: SlotNodes
  /** The shell's rail is expanded; rows a product puts in it draw for this, not their own sidebar's state. */
  railOpen: boolean
  /** Marks a slot as filled for as long as the product shows it; returns the release. */
  claim: (slot: ShellSlot) => () => void
  /** Where Insurance goes while Insure's app is not in the shell, which only Bookkeeper knows; null clears it. */
  setInsuranceHref: (href: string | null) => void
  /** Shuts the shell's phone menu once a product's row in it is picked. */
  closeMenu: () => void
  /** The customer a product has open, named atop the phone menu over Insure's org; null clears it. */
  setMenuOrgName: (name: string | null) => void
  /** A product signed the browser in without a page load, so the shell should look again for Cai. */
  onSignedIn: () => void
  /** Back to the top of the page; the shell's panel scrolls, not the window. */
  scrollToTop: () => void
}

export const ProductShellContext = createContext<ProductShell | null>(null)

/** Cai's chat is docked beside this page, so a product's own Ask Cai composers and buttons step aside. */
export function useCaiDocked(): boolean {
  return !!useContext(ProductShellContext)?.caiDocked
}

/** The shared shell around this product, or null where the product draws its own frame. */
export function useProductShell(): ProductShell | null {
  return useContext(ProductShellContext)
}

/** Scrolls the page to its top: the shell's panel inside it, the window outside it. Stable across renders. */
export function usePageScrollToTop(): () => void {
  const shell = useProductShell()
  const latest = useRef(shell)
  useLayoutEffect(() => {
    latest.current = shell
  })
  return useCallback(() => {
    if (latest.current) latest.current.scrollToTop()
    else window.scrollTo({ top: 0 })
  }, [])
}

/** The shell's side: a ref per slot to render into, and which slots a product has filled. */
export function useShellSlots() {
  const [slots, setSlots] = useState<SlotNodes>({})
  const [claims, setClaims] = useState<Partial<Record<ShellSlot, number>>>({})
  const [insuranceHref, setInsuranceHref] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const [menuOrgName, setMenuOrgName] = useState<string | null>(null)

  const refs = useMemo(
    () =>
      Object.fromEntries(
        SLOTS.map((slot) => [
          slot,
          (node: HTMLElement | null) =>
            setSlots((previous) => (previous[slot] === node ? previous : { ...previous, [slot]: node })),
        ])
      ) as Record<ShellSlot, (node: HTMLElement | null) => void>,
    []
  )

  const claim = useCallback((slot: ShellSlot) => {
    setClaims((previous) => ({ ...previous, [slot]: (previous[slot] ?? 0) + 1 }))
    return () => setClaims((previous) => ({ ...previous, [slot]: (previous[slot] ?? 1) - 1 }))
  }, [])

  const claimed = (slot: ShellSlot) => (claims[slot] ?? 0) > 0
  return {
    slots,
    refs,
    claim,
    claimed,
    insuranceHref,
    setInsuranceHref,
    menuOpen,
    setMenuOpen,
    closeMenu,
    menuOrgName,
    setMenuOrgName,
  }
}

/** Puts a product's own top-bar pieces into the shared shell's bar, keeping the product's React context. */
export function ShellSlots(pieces: Partial<Record<ShellSlot, ReactNode>>) {
  const shell = useProductShell()
  const filled = SLOTS.filter((slot) => pieces[slot] != null)
  const filledKey = filled.join(',')

  useLayoutEffect(() => {
    if (!shell) return
    const releases = filledKey.split(',').flatMap((slot) => (slot ? [shell.claim(slot as ShellSlot)] : []))
    return () => releases.forEach((release) => release())
    // claim is stable; re-claim only when which slots are filled changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledKey])

  if (!shell) return null
  return (
    <>
      {filled.map((slot) => {
        const node = shell.slots[slot]
        return node ? createPortal(pieces[slot], node, slot) : null
      })}
    </>
  )
}

/** Puts a product's rows into the shell's one rail, which stays mounted as the browser moves between products. */
export function ShellRail({ children }: { children: ReactNode }) {
  const shell = useProductShell()
  // A portal keeps the product's context, so the rows hear the shell's open state from here.
  return (
    <ShellSlots
      rail={
        <SidebarOpenContext.Provider value={shell?.railOpen ?? true}>{children}</SidebarOpenContext.Provider>
      }
    />
  )
}
